import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, Users, Camera, Calendar, BarChart3 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.js';
import { eventsService, statsService } from '../../services/firestoreService.js';
import { formatEventDate } from '../../utils/dateUtils.js';
import { StatsCard } from './StatsCard.js';
import { EventCard } from './EventCard.js';
import { RecentActivity } from './RecentActivity.js';
import { CreateEventModal } from './CreateEventModal.js';
import EditEventModal from './EditEventModal.js';

export const Dashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    upcomingEvents: 0,
    completedEvents: 0,
    totalPhotos: 0,
    totalParticipants: 0
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Cargar datos del organizador
  useEffect(() => {
    const loadOrganizerData = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        
        // Cargar eventos y estadísticas en paralelo
        const [organizerEvents, organizerStats] = await Promise.all([
          eventsService.getOrganizerEvents(user.uid),
          statsService.getOrganizerStats(user.uid)
        ]);
        
        setEvents(organizerEvents);
        setStats(organizerStats);
      } catch (error) {
        console.error('Error loading organizer data:', error);
        // En caso de error, usar datos mock como fallback
        setEvents([
          {
            eventId: 1,
            name: 'Fiesta de 15 - María González',
            status: 'active',
            date: '2024-09-15',
            location: 'Salón Principal',
            participants: 150,
            photos: 89,
            joinCode: 'MARIA15'
          },
          {
            eventId: 2,
            name: 'Casamiento - Juan y Ana',
            status: 'upcoming',
            date: '2024-09-20',
            location: 'Jardín Botánico',
            participants: 200,
            photos: 0,
            joinCode: 'JUANANA'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizerData();
  }, [user?.uid]);

  // Recargar datos después de crear un evento
  const handleEventCreated = async () => {
    if (!user?.uid) return;
    
    try {
      const [organizerEvents, organizerStats] = await Promise.all([
        eventsService.getOrganizerEvents(user.uid),
        statsService.getOrganizerStats(user.uid)
      ]);
      
      setEvents(organizerEvents);
      setStats(organizerStats);
    } catch (error) {
      console.error('Error reloading data after event creation:', error);
    }
  };

  // Recargar datos después de editar un evento
  const handleEventUpdated = async () => {
    if (!user?.uid) return;
    
    try {
      const [organizerEvents, organizerStats] = await Promise.all([
        eventsService.getOrganizerEvents(user.uid),
        statsService.getOrganizerStats(user.uid)
      ]);
      
      setEvents(organizerEvents);
      setStats(organizerStats);
    } catch (error) {
      console.error('Error reloading data after event update:', error);
    }
  };

  // Manejar la edición de un evento
  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setIsEditModalOpen(true);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedEvent(null);
  };

  // Datos para las tarjetas de estadísticas
  const statsCards = [
    {
      title: 'Eventos Activos',
      value: stats.activeEvents.toString(),
      change: null,
      changeType: 'positive',
      icon: Calendar,
      color: 'blue'
    },
    {
      title: 'Total Participantes',
      value: stats.totalParticipants.toLocaleString(),
      change: (stats.participantsGrowthPct !== undefined)
        ? `${stats.participantsGrowthPct>=0?'+':''}${stats.participantsGrowthPct}%`
        : null,
      changeType: (stats.participantsGrowthPct||0) >= 0 ? 'positive' : 'negative',
      icon: Users,
      color: 'green'
    },
    {
      title: 'Fotos Compartidas',
      value: stats.totalPhotos.toLocaleString(),
      change: (stats.photosGrowthPct !== undefined)
        ? `${stats.photosGrowthPct>=0?'+':''}${stats.photosGrowthPct}%`
        : null,
      changeType: (stats.photosGrowthPct||0) >= 0 ? 'positive' : 'negative',
      icon: Camera,
      color: 'purple'
    },
    {
      title: 'Crecimiento Mensual',
      value: `${Math.max(0, stats.photosGrowthPct || 0)}%`,
      change: (stats.participantsGrowthPct !== undefined)
        ? `${stats.participantsGrowthPct>=0?'+':''}${stats.participantsGrowthPct}%`
        : null,
      changeType: (stats.participantsGrowthPct||0) >= 0 ? 'positive' : 'negative',
      icon: TrendingUp,
      color: 'orange'
    }
  ];

  // Actividad reciente basada en eventos reales
  const activities = events.slice(0, 3).map((event, index) => ({
    id: index + 1,
    type: 'event_created',
    message: `Evento "${event.name}" ${event.status === 'active' ? 'activado' : event.status === 'upcoming' ? 'programado' : 'completado'}`,
    time: event.createdAt ? formatEventDate(event.createdAt) : 'Recientemente',
    user: user?.displayName || 'Organizador'
  }));

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Dashboard
        </h1>
        <p className="text-gray-600">
          Gestiona tus eventos y monitorea la actividad en tiempo real
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Acciones Rápidas
          </h2>
        </div>
        <div className="flex flex-wrap gap-4">
          <Link to="/events" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Gestionar Eventos
          </Link>
          <Link to="/reports" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Ver Reportes
          </Link>
        </div>
      </div>

      {/* Activity */}
      <div className="grid grid-cols-1 gap-8">
        {/* Recent Activity */}
        <div className="col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Actividad Reciente
            </h2>
            <a href="/reports" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
              Ver reportes →
            </a>
          </div>
          <RecentActivity activities={activities} />
        </div>
      </div>

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEventCreated={handleEventCreated}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        event={selectedEvent}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onEventUpdated={handleEventUpdated}
      />
    </div>
  );
};
